import React, { useMemo } from 'react';
import styled from 'styled-components';
import { HSpace } from '@/components/spacing';
import {
  RecommendationType,
  RecommendationType as T,
} from '@/webapi/use-widget-catalog-api';
import { SlotNum } from '@/features/editor/widgets/custom-widget/loading-section/shared/product-tile';
import { SlotActions } from '@/features/editor/widgets/custom-widget/loading-section/shared/slot-actions';
import { CHECKOUT_SUPPORTED_UPSELL_STRATEGIES } from '@/features/editor/widgets/checkout-extensibility/loading/checkout-supported-strategies';
import { maybe } from '@/features/details/utils';

const colors = [
  `#622CF6`,
  `#FF7F00`,
  `#00CED1`,
  `#FF1493`,
  `#008000`,
  `#FFD700`,
  `#800080`,
  `#FF4500`,
  `#4169E1`,
  `#DC143C`,
  `#0BCDAE`,
];

const strategyColors = new Map<RecommendationType, string>([
  [RecommendationType.PAST_PURCHASES, colors[0]],
  [RecommendationType.PURCHASED_TOGETHER, colors[1]],
  [RecommendationType.NEW_ARRIVALS, colors[2]],
  [RecommendationType.RECENTLY_VIEWED, colors[3]],
  [RecommendationType.CART_ITEMS, colors[4]],
  [RecommendationType.VIEWD_TOGETHER, colors[6]],
  [RecommendationType.PERSONALIZED, colors[7]],
  [RecommendationType.MOST_POPULAR, colors[8]],
  [RecommendationType.RECENTLY_VIEWED, colors[9]],
  [RecommendationType.PURCHASED_WITH_RECENTLY_PURCHASED, colors[5]],
  [RecommendationType.VIEWED_WITH_RECENTLY_VIEWED, colors[10]],
]);

const additionalStrategyDescriptions = new Map<RecommendationType, string>([
  [RecommendationType.CART_ITEMS, `Present items from the users shopping cart`],
  [
    RecommendationType.PURCHASED_WITH_RECENTLY_PURCHASED,
    `Items often purchased with the user's last purchased items`,
  ],
  [
    RecommendationType.VIEWED_WITH_RECENTLY_VIEWED,
    `Items typically viewed in the same session as the user's last viewed items`,
  ],
]);

type Props = {
  strategy: T;
  onClick?: () => void;
  slotNum?: number;
  filterLen: number;
  onDelete: () => void;
  onMoreFilters: () => void;
  draggableAttributes?: any;
  draggableListeners?: any;
  index?: number;
};

export function StrategyTile({
  slotNum,
  strategy,
  filterLen,
  onDelete,
  onMoreFilters,
  draggableAttributes,
  draggableListeners,
  index,
  onClick,
}: Props) {
  const description = useMemo(
    () =>
      additionalStrategyDescriptions.get(strategy) ||
      maybe(
        () =>
          CHECKOUT_SUPPORTED_UPSELL_STRATEGIES.find(
            (s) => s.value.type === strategy,
          )?.description,
      ),
    [strategy],
  );
  return (
    <Item>
      <StrategyWrapper>
        <Box style={{ color: strategyColors.get(strategy) || `#3451fa` }}>
          <span>
            {strategy
              .toString()
              .split(`_`)
              .filter((word) => word.length > 0)
              .map((word) => word[0])
              .slice(0, 2)
              .join(``)}
          </span>
        </Box>
        <HSpace value={2} />
        <StrategyDetails>
          <StrategyTitle>{formatStrategy(strategy)}</StrategyTitle>
          <StrategyDescription>{description}</StrategyDescription>
        </StrategyDetails>
        {slotNum >= 0 && <SlotNum>{index + 1}</SlotNum>}
      </StrategyWrapper>
      {slotNum >= 0 && (
        <SlotActions
          slotNum={slotNum}
          onDelete={onDelete}
          onMoreFilters={onMoreFilters}
          filterLen={filterLen}
          onClick={onClick}
          draggableAttributes={draggableAttributes}
          draggableListeners={draggableListeners}
        />
      )}
    </Item>
  );
}

const StrategyTitle = styled.span`
  text-transform: lowercase;
  font-family: 'Inter', serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: #000;
  line-height: 1.71;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  white-space: pre-wrap;
  &::first-letter {
    text-transform: uppercase;
  }
`;
const StrategyDescription = styled.span`
  margin-top: 0.5rem;
  line-height: 1.71;
  font-family: 'Inter', serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: #000;
`;
const Box = styled.div`
  font-size: 3.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 35%;
  min-width: 35%;
  aspect-ratio: 1;
  align-self: center;
  height: fit-content;
  border-radius: 10px;
  background-color: #e1e6e8;

  font-family: 'Inter', serif;
  font-weight: normal;
  letter-spacing: -0.94px;
`;
const StrategyDetails = styled.div`
  font-family: 'JetBrains Mono', serif;
  display: flex;
  justify-content: center;
  align-items: start;
  flex-direction: column;
`;
const StrategyWrapper = styled.div`
  display: flex;
  width: 100%;
  padding-right: 20px;
  flex: 1;
  flex-direction: row;
`;
export const Item = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 4% 4% 2.5%;
  border-radius: 8px;
  min-width: 33rem;
  min-height: 100%;
  justify-content: space-between;
  background-color: #f5f5f5;
`;

function formatStrategy(strategy: RecommendationType): string {
  if (strategy === RecommendationType.PAST_PURCHASES) {
    return `PURCHASED ITEMS`;
  }
  if (strategy === RecommendationType.VIEWD_TOGETHER) {
    return `VIEWED TOGETHER`;
  }
  return strategy.replaceAll(`_`, ` `);
}
